// frontend/src/pages/demo/RagAdmin.js
import React, { useState } from 'react';
import { API_BASE } from 'config';

export default function RagAdmin() {
  // CSV ingest state
  const [csvFile, setCsvFile] = useState(null);
  const [csvStatus, setCsvStatus] = useState('');
  const [csvLoading, setCsvLoading] = useState(false);

  // Page ingest state
  const [pageUrlsText, setPageUrlsText] = useState('');
  const [pageStatus, setPageStatus] = useState('');
  const [pageLoading, setPageLoading] = useState(false);

  async function ingestCsvAsText(csvText, filename) {
    setCsvStatus('Uploading CSV and embedding…');
    setCsvLoading(true);
    try {
      const res = await fetch(`${API_BASE}/rag/ingest`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          csv: csvText,
          tenantId: 'public-demo', // ecommerce / public demo tenant
        }),
      });

      if (!res.ok) {
        const text = await res.text().catch(() => '');
        throw new Error(text || `Ingest failed: ${res.status}`);
      }

      const j = await res.json();
      setCsvStatus(`CSV ingest complete: ingested ${j.ingested ?? 'unknown'} rows.`);
    } catch (err) {
      console.error('CSV ingest error', err);
      setCsvStatus('Error: ' + (err.message || 'CSV ingest failed'));
    } finally {
      setCsvLoading(false);
    }
  }

  function onCsvSubmit(e) {
    e.preventDefault();
    setCsvStatus('');
    if (!csvFile) {
      setCsvStatus('Pick a CSV file first.');
      return;
    }
    const reader = new FileReader();
    reader.onload = (ev) => {
      const csvText = ev.target.result;
      ingestCsvAsText(csvText, csvFile.name);
    };
    reader.readAsText(csvFile);
  }

  async function onPageSubmit(e) {
    e.preventDefault();
    setPageStatus('');
    const urls = pageUrlsText
      .split(/\r?\n/)
      .map((s) => s.trim())
      .filter(Boolean);

    if (urls.length === 0) {
      setPageStatus('Add at least one URL.');
      return;
    }

    setPageLoading(true);
    try {
      const res = await fetch(`${API_BASE}/rag/ingest-pages`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          urls,
          tenantId: 'public-demo', // same tenant for page-based ingest
        }),
      });

      const text = await res.text().catch(() => '');
      let payload = {};
      try {
        payload = text ? JSON.parse(text) : {};
      } catch {
        // if it wasn't JSON, show raw text
        payload = { raw: text };
      }

      if (!res.ok) {
        throw new Error(payload.error || text || `HTTP ${res.status}`);
      }

      const ingested = payload.rowsIngested ?? payload.ingested ?? 'unknown';
      setPageStatus(
        `Page ingest complete: ${ingested} chunks indexed from ${
          payload.urlsCount ?? urls.length
        } URL(s).`
      );
    } catch (err) {
      console.error('page ingest error', err);
      setPageStatus('Error: ' + (err.message || 'page ingest failed'));
    } finally {
      setPageLoading(false);
    }
  }

  return (
    <div className="p-8 max-w-4xl mx-auto">
      <h1 className="text-2xl mb-2">RAG Admin</h1>
      <p className="text-gray-700 mb-6 text-sm">
        Internal tools for managing the demo RAG index. Use CSV for quick product catalogs, or
        the URL crawler to index key pages before a pilot. This page isn’t linked in the main nav.
      </p>

      {/* CSV Ingest */}
      <section className="mb-8 bg-white border rounded p-4 shadow-sm">
        <h2 className="text-lg font-semibold mb-2">Ingest CSV (products or FAQs)</h2>
        <p className="text-sm text-gray-700 mb-3">
          Upload a CSV with columns:{' '}
          <code className="bg-gray-100 px-1 rounded">id</code>,{' '}
          <code className="bg-gray-100 px-1 rounded">title</code>,{' '}
          <code className="bg-gray-100 px-1 rounded">description</code>,{' '}
          <code className="bg-gray-100 px-1 rounded">url</code>. The file will be embedded and
          pushed to the vector index.
        </p>

        <form onSubmit={onCsvSubmit} className="space-y-3">
          <input
            type="file"
            accept=".csv,text/csv"
            onChange={(e) => setCsvFile(e.target.files?.[0] || null)}
          />
          {csvFile && (
            <div className="text-xs text-gray-600">
              Selected: <span className="font-medium">{csvFile.name}</span>
            </div>
          )}

          <button
            className="px-4 py-2 bg-indigo-600 text-white rounded hover:bg-indigo-700 disabled:opacity-60"
            type="submit"
            disabled={csvLoading}
          >
            {csvLoading ? 'Ingesting CSV…' : 'Ingest CSV'}
          </button>

          {csvStatus && (
            <div className="text-sm mt-2 text-gray-800">
              Status: {csvStatus}
            </div>
          )}
        </form>
      </section>

      {/* Page / URL Ingest */}
      <section className="bg-white border rounded p-4 shadow-sm">
        <h2 className="text-lg font-semibold mb-2">Index Pages via URL</h2>
        <p className="text-sm text-gray-700 mb-3">
          Paste up to ~10 key URLs (product pages, docs, FAQs). The crawler will fetch the HTML,
          extract main content, chunk it, and send it to the same embed + vector pipeline.
        </p>

        <form onSubmit={onPageSubmit} className="space-y-3">
          <textarea
            value={pageUrlsText}
            onChange={(e) => setPageUrlsText(e.target.value)}
            rows={4}
            className="w-full border rounded p-2 text-sm"
            placeholder={
              'https://example.com/product/1\nhttps://example.com/help/shipping\nhttps://example.com/returns'
            }
          />

          <button
            className="px-4 py-2 bg-indigo-600 text-white rounded hover:bg-indigo-700 disabled:opacity-60"
            type="submit"
            disabled={pageLoading}
          >
            {pageLoading ? 'Indexing pages…' : 'Index Pages'}
          </button>

          {pageStatus && (
            <div className="text-sm mt-2 text-gray-800">
              Status: {pageStatus}
            </div>
          )}
        </form>
      </section>
    </div>
  );
}
